import React, { useState, useEffect } from 'react';
import { getCompanyProfileByUserId, updateCompanyProfile, uploadCompanyLogo, uploadCompanyImages } from '../services/companyService';
import CompanyProfileForm from '../components/CompanyProfileForm';
import Footer from '../components/Footer';

const CompanyProfileManagement = ({ user, userData }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        setLoading(true);

        // Get company profile by user ID
        const result = await getCompanyProfileByUserId(user.uid);
        if (result.success) {
          setCompany(result.data);
        } else {
          setError('No company profile found. Please create one.');
        }
      } catch (error) {
        setError('Failed to load company profile');
        console.error('Error fetching company profile:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchCompanyProfile();
    }
  }, [user]);

  const handleUpdateProfile = async (updatedData) => {
    try {
      setEditing(true);
      setError('');
      setSuccess('');

      const result = await updateCompanyProfile(user.uid, updatedData);

      if (result.success) {
        setCompany(prev => ({ ...prev, ...updatedData }));
        setSuccess('Company profile updated successfully!');
        setShowEditForm(false);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An error occurred while updating the profile');
      console.error('Update error:', error);
    } finally {
      setEditing(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      setEditing(true);
      setError('');

      const result = await uploadCompanyLogo(file, user.uid);

      if (result.success) {
        const updatedData = { logoUrl: result.url };
        await handleUpdateProfile(updatedData);
        setSuccess('Logo updated successfully!');
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to upload logo');
      console.error('Logo upload error:', error);
    } finally {
      setEditing(false);
    }
  };

  const handleImagesUpload = async (files) => {
    try {
      setEditing(true);
      setError('');

      const result = await uploadCompanyImages(files, user.uid);

      if (result.success) {
        const updatedData = { companyImages: [...(company.companyImages || []), ...result.urls] };
        await handleUpdateProfile(updatedData);
        setSuccess('Images uploaded successfully!');
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to upload images');
      console.error('Images upload error:', error);
    } finally {
      setEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !company) {
    return (
      <div className="max-w-4xl mx-auto p-6 pb-24 lg:pb-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
        <CompanyProfileForm user={user} onComplete={(companyId) => {
          setCompany({ id: companyId });
          setError('');
        }} />
      </div>
    );
  }

  if (showEditForm) {
    return (
      <div className="max-w-4xl mx-auto p-6 pb-24 lg:pb-6">
        <div className="mb-6">
          <button
            onClick={() => setShowEditForm(false)}
            className="text-blue-600 hover:text-blue-800 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Profile
          </button>
        </div>

        <CompanyProfileForm
          user={user}
          initialData={company}
          onComplete={(companyId) => {
            setShowEditForm(false);
            setSuccess('Profile updated successfully!');
            // Refresh the company data
            const fetchUpdatedProfile = async () => {
              const result = await getCompanyProfileByUserId(user.uid);
              if (result.success) {
                setCompany(result.data);
              }
            };
            fetchUpdatedProfile();
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-24 lg:pb-0">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Company Profile</h1>
                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">Manage your company information and branding.</p>
              </div>
              <button
                onClick={() => setShowEditForm(true)}
                className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg sm:rounded-md hover:bg-blue-700 flex items-center text-sm sm:text-base justify-center sm:justify-start"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Profile
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md mb-6">
              {success}
            </div>
          )}

          {/* Profile Overview */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-4 sm:mb-6 lg:mb-8">
            {/* Cover Image - Using Logo */}
            <div className="h-32 sm:h-40 lg:h-48 bg-gradient-to-r from-blue-600 to-purple-600 relative">
              {company?.logoUrl && (
                <img
                  src={company.logoUrl}
                  alt="Company logo"
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>

            {/* Company Info */}
            <div className="relative px-3 sm:px-4 lg:px-6 pb-3 sm:pb-4 lg:pb-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-6">
                <div className="flex flex-col md:flex-row items-center md:items-end gap-3 sm:gap-4 -mt-8 sm:-mt-10 lg:-mt-12 xl:-mt-16">
                  {/* Company Logo */}
                  <div className="bg-white p-1.5 sm:p-2 rounded-lg shadow-lg relative">
                    {company?.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={`${company.companyName} logo`}
                        className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 object-contain"
                      />
                    ) : (
                      <div className="h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-xl sm:text-2xl font-bold text-gray-400">
                          {company?.companyName?.charAt(0)}
                        </span>
                      </div>
                    )}

                    {/* Logo Upload Button */}
                    <label className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-blue-600 text-white rounded-full p-1.5 sm:p-2 cursor-pointer hover:bg-blue-700">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleLogoUpload(e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                  </div>

                  <div className="text-gray-900 text-center md:text-left">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">{company?.companyName}</h1>
                    <p className="text-sm sm:text-base lg:text-lg opacity-90">{company?.industry}</p>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 sm:gap-3 lg:gap-4 mt-1.5 sm:mt-2 text-xs sm:text-sm">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        {company?.location}
                      </span>
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                        </svg>
                        {company?.companySize}
                      </span>
                      {company?.foundedYear && (
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          Founded {company.foundedYear}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Profile Completion */}
                <div className="mt-3 sm:mt-4 md:mt-0">
                  <div className="bg-white bg-opacity-90 rounded-lg p-3 sm:p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Profile Completion</h3>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${company?.profileCompletion || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{company?.profileCompletion || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
              {/* About */}
              <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
                <div className="flex justify-between items-center mb-3 sm:mb-4">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">About {company?.companyName}</h2>
                  <button
                    onClick={() => setShowEditForm(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Edit
                  </button>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {company?.description || 'No description provided. Click edit to add one.'}
                </p>
              </div>

              {/* Mission & Vision */}
              {/* <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Mission & Vision</h2>
                <button
                  onClick={() => setShowEditForm(true)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
              </div>

              {company?.mission && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">Mission</h3>
                  <p className="text-gray-700">{company.mission}</p>
                </div>
              )}

              {company?.vision && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Vision</h3>
                  <p className="text-gray-700">{company.vision}</p>
                </div>
              )}

              {!company?.mission && !company?.vision && (
                <p className="text-gray-500 italic">No mission or vision provided. Click edit to add them.</p>
              )}
            </div> */}

              {/* Company Culture */}
              {/* <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Culture & Benefits</h2>
                <button
                  onClick={() => setShowEditForm(true)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
              </div>

              {company?.culture && (
                <div className="mb-4">
                  <h3 className="font-medium text-gray-900 mb-2">Company Culture</h3>
                  <p className="text-gray-700">{company.culture}</p>
                </div>
              )}

              {company?.benefits && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Employee Benefits</h3>
                  <p className="text-gray-700">{company.benefits}</p>
                </div>
              )}

              {!company?.culture && !company?.benefits && (
                <p className="text-gray-500 italic">No culture or benefits information provided. Click edit to add them.</p>
              )}
            </div> */}

              {/* Company Images */}
              <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
                <div className="flex justify-between items-center mb-3 sm:mb-4">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Company Photos</h2>
                  <div className="flex space-x-2">
                    <label className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 cursor-pointer">
                      Add Photos
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            handleImagesUpload(Array.from(e.target.files));
                          }
                        }}
                      />
                    </label>
                    <button
                      onClick={() => setShowEditForm(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Manage
                    </button>
                  </div>
                </div>

                {company?.companyImages && company.companyImages.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {company.companyImages.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Company photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No company photos uploaded yet. Click "Add Photos" to upload some.</p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Company Info */}
              <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Industry</span>
                    <p className="text-gray-900">{company?.industry || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Company Size</span>
                    <p className="text-gray-900">{company?.companySize || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Company Type</span>
                    <p className="text-gray-900">{company?.companyType || 'Not specified'}</p>
                  </div>
                  {company?.foundedYear && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Founded</span>
                      <p className="text-gray-900">{company.foundedYear}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-500">Location</span>
                    <p className="text-gray-900">{company?.location || 'Not specified'}</p>
                  </div>
                  {company?.website && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Website</span>
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {company.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-3">
                  {company?.email && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Email</span>
                      <p className="text-gray-900">{company.email}</p>
                    </div>
                  )}
                  {company?.phone && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Phone</span>
                      <p className="text-gray-900">{company.phone}</p>
                    </div>
                  )}
                  {company?.whatsapp && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">WhatsApp</span>
                      <p className="text-gray-900">{company.whatsapp}</p>
                    </div>
                  )}
                  {(company?.address || company?.district || company?.state) && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Address</span>
                      {company.address && <p className="text-gray-900">{company.address}</p>}
                      <p className="text-gray-900">
                        {[
                          company.taluka,
                          company.district,
                          company.state,
                          company.pincode ? `Pincode: ${company.pincode}` : '',
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media */}
              {/* <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media</h3>
              <div className="space-y-3">
                {company?.linkedinUrl && (
                  <a
                    href={company.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    LinkedIn
                  </a>
                )}
                {company?.twitterUrl && (
                  <a
                    href={company.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-400 hover:text-blue-600"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    Twitter
                  </a>
                )}
                {company?.facebookUrl && (
                  <a
                    href={company.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    Facebook
                  </a>
                )}
                {company?.instagramUrl && (
                  <a
                    href={company.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-pink-600 hover:text-pink-800"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.014 5.367 18.647.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.807-.875-1.297-2.026-1.297-3.323s.49-2.448 1.297-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.807.875 1.297 2.026 1.297 3.323s-.49 2.448-1.297 3.323z" />
                    </svg>
                    Instagram
                  </a>
                )}

                {!company?.linkedinUrl && !company?.twitterUrl && !company?.facebookUrl && !company?.instagramUrl && (
                  <p className="text-gray-500 italic">No social media links added. Click edit to add them.</p>
                )}
              </div>
            </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Hidden on mobile */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </>
  );
};

export default CompanyProfileManagement;
